// blendModeProtocol.js
// レイヤーブレンドモードプロトコル
// 使用例（2レイヤー）: blend://multiply/{z}/{x}/{y}.png?base=https://.../{z}/{x}/{y}.png&overlay=https://.../{z}/{x}/{y}.png&overlayMaxzoom=16
// 使用例（3レイヤー）: blend://multiply/{z}/{x}/{y}.png?base=...&overlay=...&overlay2=...&overlay2Mode=soft-light&overlay2Maxzoom=15
// PMTiles対応: overlay URLが pmtiles:// で始まる場合、PMTilesライブラリでタイル取得
//   例: overlay=pmtiles://https://kenzkenz.net/dem/1m/rrim.pmtiles

import { PMTiles } from 'pmtiles'

// PMTilesリーダーキャッシュ
const _pmtilesCache = {}

// タイル座標計算のヘルパー関数（superResProtocol等からも利用）
export function calculateTileCoords(z, x, y, maxzoom) {
  if (maxzoom === null || z <= maxzoom) {
    return { tileZ: z, tileX: x, tileY: y, crop: null }
  }
  // maxzoomを超えている場合、低いズームのタイルの一部を切り出す
  const zoomDiff = z - maxzoom
  const scale = Math.pow(2, zoomDiff)
  const tileZ = maxzoom
  const tileX = Math.floor(x / scale)
  const tileY = Math.floor(y / scale)
  // 切り出し範囲（タイル内のどの部分か）
  const offsetX = x - tileX * scale
  const offsetY = y - tileY * scale
  const tileSize = 256
  const cropSize = tileSize / scale
  return {
    tileZ,
    tileX,
    tileY,
    crop: {
      sx: offsetX * cropSize,
      sy: offsetY * cropSize,
      sw: cropSize,
      sh: cropSize,
    },
  }
}

// 画像をCanvasに描画するヘルパー関数（superResProtocol等からも利用）
export function drawImageWithCrop(ctx, img, crop, width, height) {
  if (crop) {
    ctx.drawImage(img, crop.sx, crop.sy, crop.sw, crop.sh, 0, 0, width, height)
  } else {
    ctx.drawImage(img, 0, 0, width, height)
  }
}

export function registerBlendModeProtocol(maplibregl) {
  maplibregl.addProtocol('blend', async (params, abortController) => {
    try {
      // URLパースして blend://multiply/{z}/{x}/{y}.png?base=...&overlay=... の形式から情報を取得
      const url = params.url.replace('blend://', '')

      // モード部分とクエリ文字列を分離
      const [pathPart, queryString] = url.split('?')

      // タイル座標を抽出 (例: multiply/14/14552/6451.png)
      const tileMatch = pathPart.match(/^([^/]+)\/(\d+)\/(\d+)\/(\d+)\.png$/)
      if (!tileMatch) {
        console.error('Invalid blend protocol URL format:', url)
        return { data: null }
      }

      const mode = tileMatch[1]
      const z = parseInt(tileMatch[2], 10)
      const x = parseInt(tileMatch[3], 10)
      const y = parseInt(tileMatch[4], 10)

      const urlParams = new URLSearchParams(queryString)
      const baseUrlTemplate = urlParams.get('base')
      const overlayUrlTemplate = urlParams.get('overlay')
      const overlayMaxzoom = urlParams.get('overlayMaxzoom') ? parseInt(urlParams.get('overlayMaxzoom'), 10) : null

      // 3レイヤー目（オプション）
      const overlay2UrlTemplate = urlParams.get('overlay2')
      const overlay2Mode = urlParams.get('overlay2Mode') || 'soft-light'
      const overlay2Maxzoom = urlParams.get('overlay2Maxzoom') ? parseInt(urlParams.get('overlay2Maxzoom'), 10) : null

      if (!baseUrlTemplate || !overlayUrlTemplate) {
        return { data: null }
      }

      // PMTilesリーダーキャッシュ（モジュールレベル）
      // ※ _pmtilesCache はregisterBlendModeProtocolのクロージャ外で定義

      // タイル取得ヘルパー: pmtiles:// URLならPMTilesライブラリ、通常URLならfetch
      async function fetchTileAsBlob(urlTemplate, tileZ, tileX, tileY, crop, signal) {
        try {
          if (urlTemplate.startsWith('pmtiles://')) {
            const pmtilesUrl = urlTemplate.replace('pmtiles://', '')
            if (!_pmtilesCache[pmtilesUrl]) {
              _pmtilesCache[pmtilesUrl] = new PMTiles(pmtilesUrl)
            }
            const pm = _pmtilesCache[pmtilesUrl]
            const resp = await pm.getZxy(tileZ, tileX, tileY)
            if (!resp || !resp.data) return null
            return new Blob([resp.data])
          }
          const url = urlTemplate.replace('{z}', tileZ).replace('{x}', tileX).replace('{y}', tileY)
          const response = await fetch(url, { signal })
          if (!response.ok) return null
          return response.blob()
        } catch (e) {
          // fetchエラーは静かに処理
          return null
        }
      }

      // baseタイル座標（常にリクエストズームレベル）
      const overlay1Coords = calculateTileCoords(z, x, y, overlayMaxzoom)

      // overlay2がある場合
      let overlay2Coords = null
      if (overlay2UrlTemplate) {
        overlay2Coords = calculateTileCoords(z, x, y, overlay2Maxzoom)
      }

      // タイル取得
      const blobPromises = [
        fetchTileAsBlob(baseUrlTemplate, z, x, y, null, abortController.signal),
        fetchTileAsBlob(overlayUrlTemplate, overlay1Coords.tileZ, overlay1Coords.tileX, overlay1Coords.tileY, overlay1Coords.crop, abortController.signal),
      ]
      if (overlay2UrlTemplate) {
        blobPromises.push(fetchTileAsBlob(overlay2UrlTemplate, overlay2Coords.tileZ, overlay2Coords.tileX, overlay2Coords.tileY, overlay2Coords.crop, abortController.signal))
      }

      const blobs = await Promise.all(blobPromises)
      if (!blobs[0]) return { data: null }
      // overlayがない場合（PMTilesで該当タイルなし等）はbaseのみ返す
      if (!blobs[1]) {
        const baseBitmap = await createImageBitmap(blobs[0])
        const canvas = new OffscreenCanvas(baseBitmap.width, baseBitmap.height)
        const ctx = canvas.getContext('2d')
        ctx.drawImage(baseBitmap, 0, 0)
        const resultBlob = await canvas.convertToBlob({ type: 'image/png' })
        return { data: await resultBlob.arrayBuffer() }
      }

      // 画像をImageBitmapとして読み込み
      const imgPromises = blobs.map((b) => b ? createImageBitmap(b) : null)
      const images = await Promise.all(imgPromises)

      const baseImg = images[0]
      const overlayImg = images[1]
      const overlay2Img = images[2] || null

      // Canvasで合成
      const canvas = new OffscreenCanvas(baseImg.width, baseImg.height)
      const ctx = canvas.getContext('2d')

      // ベース画像を描画
      ctx.drawImage(baseImg, 0, 0)

      // overlay1をブレンド
      ctx.globalCompositeOperation = mode
      drawImageWithCrop(ctx, overlayImg, overlay1Coords.crop, baseImg.width, baseImg.height)

      // overlay2があればさらにブレンド
      if (overlay2Img) {
        ctx.globalCompositeOperation = overlay2Mode
        drawImageWithCrop(ctx, overlay2Img, overlay2Coords.crop, baseImg.width, baseImg.height)
      }

      // 結果をBlobに変換
      const resultBlob = await canvas.convertToBlob({ type: 'image/png' })
      const arrayBuffer = await resultBlob.arrayBuffer()

      return { data: arrayBuffer }
    } catch (error) {
      console.error('Blend protocol error:', error)
      return { data: null }
    }
  })
}

// 利用可能なブレンドモード一覧
export const BLEND_MODES = [
  { value: 'normal', label: '通常' },
  { value: 'multiply', label: '乗算' },
  { value: 'screen', label: 'スクリーン' },
  { value: 'overlay', label: 'オーバーレイ' },
  { value: 'darken', label: '暗く' },
  { value: 'lighten', label: '明るく' },
  { value: 'color-dodge', label: '覆い焼き' },
  { value: 'color-burn', label: '焼き込み' },
  { value: 'hard-light', label: 'ハードライト' },
  { value: 'soft-light', label: 'ソフトライト' },
  { value: 'difference', label: '差の絶対値' },
  { value: 'exclusion', label: '除外' },
]
